import * as React from "react";

import { aiClient, aiRouter } from "@editor/trpc/ai";
import { analyticsClient, analyticsRouter } from "@editor/trpc/analytics";
import {
  analyticsLinkClient,
  analyticsLinkRouter,
} from "@editor/trpc/analyticsLink";
import {
  announcementClient,
  announcementRouter,
} from "@editor/trpc/announcement";
import { assetClient, assetRouter } from "@editor/trpc/asset";
import { authClient, authRouter } from "@editor/trpc/auth";
import { billingClient, billingRouter } from "@editor/trpc/billing";
import { chatClient, chatRouter } from "@editor/trpc/chat";
import {
  codeExecutionClient,
  codeExecutionRouter,
} from "@editor/trpc/codeExecution";
import {
  componentTemplatesClient,
  componentTemplatesRouter,
} from "@editor/trpc/componentTemplates";
import {
  designLibraryClient,
  designLibraryRouter,
} from "@editor/trpc/designLibrary";
import { elementClient, elementRouter } from "@editor/trpc/element";
import { entriClient, entriRouter } from "@editor/trpc/entri";
import { experimentClient, experimentRouter } from "@editor/trpc/experiment";
import {
  fileTemplateClient,
  fileTemplateRouter,
} from "@editor/trpc/fileTemplate";
import { fileVersionClient, fileVersionRouter } from "@editor/trpc/fileVersion";
import { folderClient, folderRouter } from "@editor/trpc/folder";
import { fontClient, fontRouter } from "@editor/trpc/font";
import { funnelClient, funnelRouter } from "@editor/trpc/funnel";
import {
  funnelTemplateClient,
  funnelTemplateRouter,
} from "@editor/trpc/funnelTemplate";
import { integrationClient, integrationRouter } from "@editor/trpc/integration";
import { marketingClient, marketingRouter } from "@editor/trpc/marketing";
import { modifiersClient, modifiersRouter } from "@editor/trpc/modifiers";
import {
  nextJSProjectClient,
  nextJSProjectRouter,
} from "@editor/trpc/nextJSProject";
import {
  nextJSProjectCacheClient,
  nextJSProjectCacheRouter,
} from "@editor/trpc/nextJSProjectCache";
import { onboardingClient, onboardingRouter } from "@editor/trpc/onboarding";
import { productClient, productRouter } from "@editor/trpc/product";
import { productFileClient, productFileRouter } from "@editor/trpc/productFile";
import { projectClient, projectRouter } from "@editor/trpc/project";
import {
  referralCodeClient,
  referralCodeRouter,
} from "@editor/trpc/referralCode";
import { shopifyClient, shopifyRouter } from "@editor/trpc/shopify";
import {
  shopMetadataClient,
  shopMetadataRouter,
} from "@editor/trpc/shopMetadata";
import { storeClient, storeRouter } from "@editor/trpc/store";
import {
  stripeConnectClient,
  stripeConnectRouter,
} from "@editor/trpc/stripeConnect";
import { stylesClient, stylesRouter } from "@editor/trpc/styles";
import {
  subscriptionClient,
  subscriptionRouter,
} from "@editor/trpc/subscription";
import { swatchClient, swatchRouter } from "@editor/trpc/swatch";
import { triggerClient, triggerRouter } from "@editor/trpc/trigger";
import { userClient, userRouter } from "@editor/trpc/user";
import {
  v2CustomDomainClient,
  v2CustomDomainRouter,
} from "@editor/trpc/v2CustomDomain";
import {
  v2ShopDeploymentClient,
  v2ShopDeploymentRouter,
} from "@editor/trpc/v2ShopDeployment";
import {
  webContainerClient,
  webContainerRouter,
} from "@editor/trpc/webContainer";
import { workspaceClient, workspaceRouter } from "@editor/trpc/workspace";
import { getNamespacedQueryClient, getQueryClient } from "@editor/utils/trpc";

import { QueryClientProvider } from "@tanstack/react-query";

/**
 * Wraps children with all TRPC-related providers required by the editor.
 */
export function TRPCProviders({ children }: React.PropsWithChildren) {
  return (
    <QueryClientProvider client={getQueryClient()}>
      <aiRouter.Provider
        client={aiClient}
        queryClient={getNamespacedQueryClient("ai")}
      >
        <analyticsRouter.Provider
          client={analyticsClient}
          queryClient={getNamespacedQueryClient("analytics")}
        >
          <analyticsLinkRouter.Provider
            client={analyticsLinkClient}
            queryClient={getNamespacedQueryClient("analyticsLink")}
          >
            <announcementRouter.Provider
              client={announcementClient}
              queryClient={getNamespacedQueryClient("announcement")}
            >
              <assetRouter.Provider
                client={assetClient}
                queryClient={getNamespacedQueryClient("asset")}
              >
                <authRouter.Provider
                  client={authClient}
                  queryClient={getNamespacedQueryClient("auth")}
                >
                  <billingRouter.Provider
                    client={billingClient}
                    queryClient={getNamespacedQueryClient("billing")}
                  >
                    <chatRouter.Provider
                      client={chatClient}
                      queryClient={getNamespacedQueryClient("chat")}
                    >
                      <codeExecutionRouter.Provider
                        client={codeExecutionClient}
                        queryClient={getNamespacedQueryClient("codeExecution")}
                      >
                        <componentTemplatesRouter.Provider
                          client={componentTemplatesClient}
                          queryClient={getNamespacedQueryClient(
                            "componentTemplates",
                          )}
                        >
                          <designLibraryRouter.Provider
                            client={designLibraryClient}
                            queryClient={getNamespacedQueryClient(
                              "designLibrary",
                            )}
                          >
                            <elementRouter.Provider
                              client={elementClient}
                              queryClient={getNamespacedQueryClient("element")}
                            >
                              <entriRouter.Provider
                                client={entriClient}
                                queryClient={getNamespacedQueryClient("entri")}
                              >
                                <experimentRouter.Provider
                                  client={experimentClient}
                                  queryClient={getNamespacedQueryClient(
                                    "experiment",
                                  )}
                                >
                                  <fileTemplateRouter.Provider
                                    client={fileTemplateClient}
                                    queryClient={getNamespacedQueryClient(
                                      "fileTemplate",
                                    )}
                                  >
                                    <fileVersionRouter.Provider
                                      client={fileVersionClient}
                                      queryClient={getNamespacedQueryClient(
                                        "fileVersion",
                                      )}
                                    >
                                      <folderRouter.Provider
                                        client={folderClient}
                                        queryClient={getNamespacedQueryClient(
                                          "folder",
                                        )}
                                      >
                                        <fontRouter.Provider
                                          client={fontClient}
                                          queryClient={getNamespacedQueryClient(
                                            "font",
                                          )}
                                        >
                                          <funnelRouter.Provider
                                            client={funnelClient}
                                            queryClient={getNamespacedQueryClient(
                                              "funnel",
                                            )}
                                          >
                                            <funnelTemplateRouter.Provider
                                              client={funnelTemplateClient}
                                              queryClient={getNamespacedQueryClient(
                                                "funnelTemplate",
                                              )}
                                            >
                                              <integrationRouter.Provider
                                                client={integrationClient}
                                                queryClient={getNamespacedQueryClient(
                                                  "integration",
                                                )}
                                              >
                                                <marketingRouter.Provider
                                                  client={marketingClient}
                                                  queryClient={getNamespacedQueryClient(
                                                    "marketing",
                                                  )}
                                                >
                                                  <modifiersRouter.Provider
                                                    client={modifiersClient}
                                                    queryClient={getNamespacedQueryClient(
                                                      "modifiers",
                                                    )}
                                                  >
                                                    <nextJSProjectRouter.Provider
                                                      client={
                                                        nextJSProjectClient
                                                      }
                                                      queryClient={getNamespacedQueryClient(
                                                        "nextJSProject",
                                                      )}
                                                    >
                                                      <nextJSProjectCacheRouter.Provider
                                                        client={
                                                          nextJSProjectCacheClient
                                                        }
                                                        queryClient={getNamespacedQueryClient(
                                                          "nextJSProjectCache",
                                                        )}
                                                      >
                                                        <onboardingRouter.Provider
                                                          client={
                                                            onboardingClient
                                                          }
                                                          queryClient={getNamespacedQueryClient(
                                                            "onboarding",
                                                          )}
                                                        >
                                                          <productRouter.Provider
                                                            client={
                                                              productClient
                                                            }
                                                            queryClient={getNamespacedQueryClient(
                                                              "product",
                                                            )}
                                                          >
                                                            <productFileRouter.Provider
                                                              client={
                                                                productFileClient
                                                              }
                                                              queryClient={getNamespacedQueryClient(
                                                                "productFile",
                                                              )}
                                                            >
                                                              <projectRouter.Provider
                                                                client={
                                                                  projectClient
                                                                }
                                                                queryClient={getNamespacedQueryClient(
                                                                  "project",
                                                                )}
                                                              >
                                                                <referralCodeRouter.Provider
                                                                  client={
                                                                    referralCodeClient
                                                                  }
                                                                  queryClient={getNamespacedQueryClient(
                                                                    "referralCode",
                                                                  )}
                                                                >
                                                                  <shopifyRouter.Provider
                                                                    client={
                                                                      shopifyClient
                                                                    }
                                                                    queryClient={getNamespacedQueryClient(
                                                                      "shopify",
                                                                    )}
                                                                  >
                                                                    <shopMetadataRouter.Provider
                                                                      client={
                                                                        shopMetadataClient
                                                                      }
                                                                      queryClient={getNamespacedQueryClient(
                                                                        "shopMetadata",
                                                                      )}
                                                                    >
                                                                      <storeRouter.Provider
                                                                        client={
                                                                          storeClient
                                                                        }
                                                                        queryClient={getNamespacedQueryClient(
                                                                          "store",
                                                                        )}
                                                                      >
                                                                        <stripeConnectRouter.Provider
                                                                          client={
                                                                            stripeConnectClient
                                                                          }
                                                                          queryClient={getNamespacedQueryClient(
                                                                            "stripeConnect",
                                                                          )}
                                                                        >
                                                                          <stylesRouter.Provider
                                                                            client={
                                                                              stylesClient
                                                                            }
                                                                            queryClient={getNamespacedQueryClient(
                                                                              "styles",
                                                                            )}
                                                                          >
                                                                            <subscriptionRouter.Provider
                                                                              client={
                                                                                subscriptionClient
                                                                              }
                                                                              queryClient={getNamespacedQueryClient(
                                                                                "subscription",
                                                                              )}
                                                                            >
                                                                              <swatchRouter.Provider
                                                                                client={
                                                                                  swatchClient
                                                                                }
                                                                                queryClient={getNamespacedQueryClient(
                                                                                  "swatch",
                                                                                )}
                                                                              >
                                                                                <triggerRouter.Provider
                                                                                  client={
                                                                                    triggerClient
                                                                                  }
                                                                                  queryClient={getNamespacedQueryClient(
                                                                                    "trigger",
                                                                                  )}
                                                                                >
                                                                                  <userRouter.Provider
                                                                                    client={
                                                                                      userClient
                                                                                    }
                                                                                    queryClient={getNamespacedQueryClient(
                                                                                      "user",
                                                                                    )}
                                                                                  >
                                                                                    <v2CustomDomainRouter.Provider
                                                                                      client={
                                                                                        v2CustomDomainClient
                                                                                      }
                                                                                      queryClient={getNamespacedQueryClient(
                                                                                        "v2CustomDomain",
                                                                                      )}
                                                                                    >
                                                                                      <v2ShopDeploymentRouter.Provider
                                                                                        client={
                                                                                          v2ShopDeploymentClient
                                                                                        }
                                                                                        queryClient={getNamespacedQueryClient(
                                                                                          "v2ShopDeployment",
                                                                                        )}
                                                                                      >
                                                                                        <webContainerRouter.Provider
                                                                                          client={
                                                                                            webContainerClient
                                                                                          }
                                                                                          queryClient={getNamespacedQueryClient(
                                                                                            "webContainer",
                                                                                          )}
                                                                                        >
                                                                                          <workspaceRouter.Provider
                                                                                            client={
                                                                                              workspaceClient
                                                                                            }
                                                                                            queryClient={getNamespacedQueryClient(
                                                                                              "workspace",
                                                                                            )}
                                                                                          >
                                                                                            {
                                                                                              children
                                                                                            }
                                                                                          </workspaceRouter.Provider>
                                                                                        </webContainerRouter.Provider>
                                                                                      </v2ShopDeploymentRouter.Provider>
                                                                                    </v2CustomDomainRouter.Provider>
                                                                                  </userRouter.Provider>
                                                                                </triggerRouter.Provider>
                                                                              </swatchRouter.Provider>
                                                                            </subscriptionRouter.Provider>
                                                                          </stylesRouter.Provider>
                                                                        </stripeConnectRouter.Provider>
                                                                      </storeRouter.Provider>
                                                                    </shopMetadataRouter.Provider>
                                                                  </shopifyRouter.Provider>
                                                                </referralCodeRouter.Provider>
                                                              </projectRouter.Provider>
                                                            </productFileRouter.Provider>
                                                          </productRouter.Provider>
                                                        </onboardingRouter.Provider>
                                                      </nextJSProjectCacheRouter.Provider>
                                                    </nextJSProjectRouter.Provider>
                                                  </modifiersRouter.Provider>
                                                </marketingRouter.Provider>
                                              </integrationRouter.Provider>
                                            </funnelTemplateRouter.Provider>
                                          </funnelRouter.Provider>
                                        </fontRouter.Provider>
                                      </folderRouter.Provider>
                                    </fileVersionRouter.Provider>
                                  </fileTemplateRouter.Provider>
                                </experimentRouter.Provider>
                              </entriRouter.Provider>
                            </elementRouter.Provider>
                          </designLibraryRouter.Provider>
                        </componentTemplatesRouter.Provider>
                      </codeExecutionRouter.Provider>
                    </chatRouter.Provider>
                  </billingRouter.Provider>
                </authRouter.Provider>
              </assetRouter.Provider>
            </announcementRouter.Provider>
          </analyticsLinkRouter.Provider>
        </analyticsRouter.Provider>
      </aiRouter.Provider>
    </QueryClientProvider>
  );
}
